import config, { getFullLocale } from '@packages/config';
import { localizedPathnameCacheKey } from '@packages/config/src/default';
import { UrqlProvider } from '@packages/gql/src/urql';
import {
  CC_CONSUMER_SHOPCONFIG_COOKIE,
  getTagManagerProps,
  type AppConfigData,
} from '@packages/modules/src/AppConfig/fetchAppConfig';
import { getPagesRouterAppConfigData } from '@packages/modules/src/AppConfig/getPagesRouterAppConfigData';
import { fetchRequiredStaticJson } from '@packages/modules/src/Header/api';
import { getNavigationDataEndpoint } from '@packages/modules/src/Header/Navigation/getNavigationDataEndpoint';
import { isMonitoringCacheKey } from '@packages/shared/src/hooks/useIsMonitoring/useIsMonitoring';
import { ConfigProvider } from '@packages/shared/src/providers/ConfigProvider/ConfigProvider';
import { CookieProvider } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { LanguageProvider } from '@packages/shared/src/providers/LanguageProvider/LanguageProvider';
import { SessionProvider } from '@packages/shared/src/providers/SessionProvider/SessionProvider';
import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { cached } from '@packages/shared/src/utils/cached/cached';
import { execParallel } from '@packages/shared/src/utils/execParallel/execParallel';
import { getLanguageFromUrl } from '@packages/shared/src/utils/getLanguageFromUrl/getLanguageFromUrl';
import { sendWebVitals } from '@packages/shared/src/utils/sendWebVitals/sendWebVitals';
import '@packages/themes/dist/app-order-default-theme.css';
import type { Device } from '@packages/shared/src/utils/types';
import { PageviewProvider } from '@packages/tracking/src/components/PageviewProvider/PageviewProvider';
import { TrackingProvider } from '@packages/tracking/src/components/TrackingProvider/TrackingProvider';
import { DeviceHeaderProvider } from '@packages/shared/src/providers/DeviceHeaderProvider/DeviceHeaderProvider';
import { PageTransitionProvider } from '@packages/shared/src/providers/PageTransitionProvider/PageTransitionProvider';
import { LocalizedPageUrlProvider } from '@packages/shared/src/providers/LocalizedPageUrlProvider/LocalizedPageUrlProvider';
import type { AppContext, AppProps, NextWebVitalsMetric } from 'next/app';
import App from 'next/app';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { parseCookies, setCookie } from 'nookies';
import { SWRConfig } from 'swr';
import { fonts } from '@packages/themes/dist/fonts';
import type { NextFont } from 'next/dist/compiled/@next/font';

import messages from '@packages/i18n/src/order/locales/compiled-messages';

interface HeaderAppProps extends AppProps {
  device: Device;
  isBot: boolean;
  cookies: Record<string, string>;
  fallback: Record<string, any> | undefined;
  /** `null` while the consumer is off or on client-side navigation (no request). */
  appConfig: AppConfigData | null;
}

// const PlaceholderSuspenseFallback = () => (
//   <div>If you see this on a live site, please kindly notify a developer</div>
// );

// const PageErrorPlaceholder = () => <div>The page just died 😢</div>;

const MyApp = ({
  Component,
  pageProps,
  device,
  cookies,
  fallback,
  isBot,
  appConfig,
}: HeaderAppProps) => {
  const pathname = usePathname();
  const language = getLanguageFromUrl(pathname ?? '');
  const {
    tenantId,
    i18n: { defaultLocale },
    captcha: { recaptchaSessionToken },
  } = config;
  // if (router.pathname = "/warenkorb") { // We can figure out our route from here

  // }
  const locale = (language && getFullLocale(language)) ?? defaultLocale;

  const { fallback: pageFallback, ...otherPageProps } = pageProps;
  const pageFallbackFinal = {
    ...pageFallback,
    // force reset of localizedPathname if not exist
    // used by LocaleSwitchSlot.tsx
    [localizedPathnameCacheKey]: pageFallback?.[localizedPathnameCacheKey] || null,
  };

  return (
    <>
      {recaptchaSessionToken !== '' && (
        <Script
          src={`https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSessionToken}&waf=session`}
          async
          defer
        />
      )}
      <LocalizedPageUrlProvider>
        <CookieProvider cookies={cookies}>
          <ConfigProvider config={config} appName="app-order">
            <SessionProvider tenantId={tenantId}>
              <UrqlProvider pageProps={pageProps}>
                <LanguageProvider
                  locale={locale}
                  defaultLocale={config.i18n.defaultLocale}
                  messages={messages[locale as keyof typeof messages]}
                  onWarn={() => null}
                >
                  <PageTransitionProvider>
                    <TrackingProvider {...getTagManagerProps(appConfig)} isBot={isBot}>
                      <PageviewProvider initialPageviewId={fallback?.pageviewId}>
                        <DeviceHeaderProvider detectedDevice={device}>
                          <SWRConfig
                            value={{
                              fallback: { ...fallback, ...pageFallbackFinal },
                              dedupingInterval: 3000,
                            }}
                          >
                            {/* TODO get useful fallback from UI/UX */}
                            <ErrorBoundary fallback={<div />}>
                              <main
                                className={`${Object.entries(fonts)
                                  .map(([, font]) => {
                                    const typedFont = font as NextFont | { variable?: string };
                                    return 'variable' in typedFont ? typedFont.variable : '';
                                  })
                                  .join(
                                    ' ',
                                  )} ${'primary' in fonts && fonts.primary ? (fonts.primary as NextFont).className : ''}`}
                              >
                                <Component device={device} {...otherPageProps} />
                              </main>
                            </ErrorBoundary>
                          </SWRConfig>
                        </DeviceHeaderProvider>
                      </PageviewProvider>
                    </TrackingProvider>
                  </PageTransitionProvider>
                </LanguageProvider>
              </UrqlProvider>
            </SessionProvider>
          </ConfigProvider>
        </CookieProvider>
      </LocalizedPageUrlProvider>
    </>
  );
};

MyApp.getInitialProps = async (appContext: AppContext) => {
  const { ctx } = appContext;
  const {
    query: { lang },
  } = ctx;
  const defaultLanguage = config.i18n.defaultLocale.split('-')[0];

  let fallback: HeaderAppProps['fallback'];
  let appConfig: HeaderAppProps['appConfig'] = null;

  const detectedDevice: Device = ctx.req?.headers['x-ua-device'] as Device;
  const isBot: boolean = ctx.req?.headers['x-ua-bot'] !== undefined;
  const isMonitoring = ctx.req?.headers['x-ua-monitoring'] !== undefined;
  const cookies = parseCookies(ctx);

  if (ctx.req) {
    const {
      clientId,
      navigation: { loadSliced, dataBucket },
    } = config;
    const language = (typeof lang === 'string' && lang) || defaultLanguage || 'de';
    const localeForCacheKey = getFullLocale(language);

    const navEndpoint = getNavigationDataEndpoint(
      dataBucket,
      clientId,
      detectedDevice === 'desktop' ? 'web' : 'mob',
      localeForCacheKey,
      loadSliced,
    );

    // The app config fetch never rejects, so it needs no `execParallel` guard.
    const [[navigation], fetchedAppConfig] = await Promise.all([
      execParallel([
        cached(navEndpoint, () => fetchRequiredStaticJson(navEndpoint, { componentName: '_app' })),
      ]),
      getPagesRouterAppConfigData(localeForCacheKey, cookies[CC_CONSUMER_SHOPCONFIG_COOKIE]),
    ]);
    appConfig = fetchedAppConfig;
    let pageviewId = cookies.GlycerinPageViewId;
    const cookieFromBackend = cookies.GlycerinPageViewIdFromBackend as string | undefined;
    if (!cookieFromBackend) {
      pageviewId = crypto.randomUUID();
      // set cookie for next request to distinguish backend-api tracking call and nextjs header
      setCookie(ctx, 'GlycerinPageViewIdFromBackend', 'writtenFromHeaderApp');
      setCookie(ctx, 'GlycerinPageViewId', pageviewId);
    }

    fallback = {
      [isMonitoringCacheKey]: isMonitoring,
      [navEndpoint]: navigation,
      pageviewId,
      deploymentEnv: process.env.DEPLOYMENT_ENV,
    };
  }

  const appProps = await App.getInitialProps(appContext);

  return {
    ...appProps,
    fallback,
    device: detectedDevice,
    cookies,
    isBot,
    appConfig,
  };
};

// Report web-vitals - see https://nextjs.org/docs/advanced-features/measuring-performance
export function reportWebVitals(metric: NextWebVitalsMetric) {
  sendWebVitals(metric);
}

// eslint-disable-next-line import-x/no-default-export
export default MyApp;
